import React, { PropsWithChildren, useEffect } from "react";
import { useTranslation } from "i18n";
import { useSessionStorage } from "usehooks-ts";
import {
  SL_MEMBER_GROUP,
  SIGN_IN_CALLBACK_URL,
  useCheckAndSetPortalCookie,
  useAxiosTokenRefresh,
  useAuth,
  useAutoSignin,
  POST_LOGIN_REDIRECT_KEY,
  UserAttributes,
} from "shared-auth";
import { Spinner } from "../../../../Atoms/Spinner/Spinner";
import CommonErrorPopup from "../../../CommonErrorPopup";
import { config } from "../../../../../config";

const Authenticator = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation();
  const [, setPostLoginRedirect] = useSessionStorage(
    POST_LOGIN_REDIRECT_KEY,
    ""
  );
  const { isLoading, isAuthenticated, error } = useAutoSignin({
    signinMethod: "signinRedirect",
  });
  const auth = useAuth();

  useAxiosTokenRefresh();

  useCheckAndSetPortalCookie();

  const signout = async () => {
    await auth.signoutSilent();
  };

  useEffect(() => {
    if (auth.activeNavigator === "signinRedirect") {
      setPostLoginRedirect(window.location.href);
    }

    if (auth.activeNavigator === "signoutSilent") {
      window.location.assign(config.portalApiLogoutUrl);
    }
  }, [auth.activeNavigator, setPostLoginRedirect]);

  if (isLoading || window.location.pathname.includes(SIGN_IN_CALLBACK_URL)) {
    return <Spinner />;
  }

  //stub for cognito users without groups
  //to be removed when cognito users are migrated to have groups SL-8055
  const profileCheckerUser =
    auth.user?.profile[UserAttributes.IdentityProvider] === "cognito";
  const userHasEmptyGroup =
    isAuthenticated &&
    !profileCheckerUser &&
    !auth.user?.profile[SL_MEMBER_GROUP];

  if (!isAuthenticated || error || userHasEmptyGroup) {
    return (
      <CommonErrorPopup
        errorStatusCode={401}
        popupProps={{
          close: signout,
        }}
        alertMessageProps={{
          buttons: [
            {
              name: t("common:button.label.close"),
              type: "primary",
              click: signout,
            },
          ],
        }}
      />
    );
  }

  return <>{children}</>;
};

export default Authenticator;
